import React from "react";
import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";
import classnames from "classnames";

import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { enrichCompanyDataWithAvatars } from "./DataEnrichment";
import EditableTags from "./EditableTags";

const TOTAL_NUMBERED_ITEM_LIMIT = 50;

// Custom hook to handle agency data enrichment
const useEnrichedAgencyData = (agencies) => {
  const [enrichedAgencies, setEnrichedAgencies] = React.useState([]);

  React.useEffect(() => {
    async function loadDetails() {
      const enriched = await enrichCompanyDataWithAvatars({
        apiDataUrl: `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/companies`, // URL for fetching company data
        data: agencies,
        extraHeaders: authenticatedHeadersHash(),
      });
      setEnrichedAgencies(enriched);
    }

    setEnrichedAgencies(agencies);
    loadDetails();
  }, [agencies]);

  return enrichedAgencies;
};

// Renders a table of AGENCY data with toggleable visibility for each row
function TopAgencyTable({ agencies, toggleRowVisibility, moveItemUp, moveItemDown, deleteItem, updateTags }) {
  const enrichedAgencies = useEnrichedAgencyData(agencies);

  const numberedEnrichedAgencies = enrichedAgencies.reduce(
    ({ items, index }, current) => {
      // Number non-hidden items up to TOTAL_NUMBERED_ITEM_LIMIT
      if (!current.hidden && index <= TOTAL_NUMBERED_ITEM_LIMIT) {
        return {
          items: [...items, { no: index, ...current }],
          index: index + 1,
        };
      }
      return {
        items: [...items, { no: null, ...current }],
        index,
      };
    },
    { items: [], index: 1 },
  ).items;

  return (
    <table className="table table-striped table-bordered">
      <thead className="thead-dark">
        <tr>
          <th>#</th>
          <th>Logo</th>
          <th>Agency</th>
          <th>Pitches</th>
          <th>Direct media reply points</th>
          <th>Pitch speed points</th>
          <th>Conversation outcome points</th>
          <th>Bad behavior points</th>
          <th>Total points</th>
          <th>Profile</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {/* Render a placeholder when there are no entries */}
        {agencies.length === 0 && (
          <tr key="no-entries">
            <td colSpan="99">
              <i>No entries</i>
            </td>
          </tr>
        )}
        {/* Render rows for each AGENCY entry */}
        {numberedEnrichedAgencies.map((agency) => (
          <tr
            key={agency.company_id}
            className={classnames({
              "table-secondary text-decoration-line-through opacity-50": agency.hidden,
              "table-success": !!agency.no,
            })}
          >
            {/* Index column */}
            <td>{agency.no}</td>
            {/* Agency avatar column */}
            <td>
              {agency.company_avatar ? (
                <img
                  src={agency.company_avatar}
                  alt="Avatar"
                  className="img-fluid"
                  style={{ width: "200px", height: "100px", objectFit: "cover" }}
                />
              ) : (
                <Skeleton width={50} height={50} />
              )}
            </td>
            {/* Agency with editable tags */}
            <td>
              <div>{agency.company_name}</div>
              <div className="mt-2">
                <EditableTags tags={agency.tags} onTagsChange={(newTags) => updateTags(agency.company_id, newTags)} />
              </div>
            </td>
            {/* Pitches column */}
            <td>{agency.num_pitches}</td>
            {/* Direct media reply points column */}
            <td>{agency.direct_media_reply_points}</td>
            {/* Pitch speed points column */}
            <td>{agency.pitch_speed_points}</td>
            {/* Conversation outcome points column */}
            <td>{agency.conversation_outcome_points}</td>
            {/* Bad behavior points column */}
            <td>{agency.bad_behavior_points}</td>
            {/* Total points column */}
            <td>{agency.total_points}</td>
            {/* Agency link column */}
            <td>
              <a
                href={`/companies/${agency.company_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-primary"
              >
                View profile
              </a>
            </td>
            {/* Action column */}
            <td className="text-nowrap">
              <button
                type="button"
                className="btn btn-sm btn-secondary me-2"
                onClick={() => toggleRowVisibility(agency.company_id)}
              >
                {agency.hidden ? "Show" : "Hide"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={() => moveItemUp(agency.company_id)}
                disabled={enrichedAgencies.findIndex((a) => a.company_id === agency.company_id) === 0}
                title="Move up"
              >
                <i className="fas fa-arrow-up" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={() => moveItemDown(agency.company_id)}
                disabled={
                  enrichedAgencies.findIndex((a) => a.company_id === agency.company_id) === enrichedAgencies.length - 1
                }
                title="Move down"
              >
                <i className="fas fa-arrow-down" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => deleteItem(agency.company_id)}
                title="Delete"
              >
                <i className="fas fa-trash" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

TopAgencyTable.propTypes = {
  agencies: PropTypes.arrayOf(
    PropTypes.shape({
      bad_behavior_points: PropTypes.number, // Points deducted for bad behavior
      company_id: PropTypes.number.isRequired, // Unique company identifier
      conversation_outcome_points: PropTypes.number, // Points earned for conversation outcomes
      direct_media_reply_points: PropTypes.number, // Points earned for direct replies
      hidden: PropTypes.bool.isRequired, // Visibility state of the row
      ineligible: PropTypes.bool, // Eligibility state
      num_pitches: PropTypes.number.isRequired, // Number of pitches made
      pitch_speed_points: PropTypes.number, // Points earned for pitch speed
      tags: PropTypes.arrayOf(PropTypes.string), // Top 5 hashtags derived from represented entities
      total_points: PropTypes.number.isRequired, // Total points scored
    }),
  ).isRequired,
  toggleRowVisibility: PropTypes.func.isRequired, // Callback to toggle row visibility
  moveItemUp: PropTypes.func.isRequired, // Callback to move item up
  moveItemDown: PropTypes.func.isRequired, // Callback to move item down
  deleteItem: PropTypes.func.isRequired, // Callback to delete item
  updateTags: PropTypes.func.isRequired, // Callback to update tags for an agency
};

export default TopAgencyTable;
